import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { API } from '@/App';
import { Users, Fuel, Settings } from 'lucide-react';
import { WhatsAppBookButton } from '@/components/WhatsAppBookButton';

const CarsPage = () => {
  const [cars, setCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: '',
    location: '',
    min_price: '',
    max_price: ''
  });

  useEffect(() => {
    fetchCars();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, cars]);

  const fetchCars = async () => {
    try {
      const response = await axios.get(`${API}/cars`);
      setCars(response.data);
      setFilteredCars(response.data);
    } catch (error) {
      toast.error('Failed to load cars');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...cars];

    if (filters.type) {
      filtered = filtered.filter(car => car.type === filters.type);
    }

    if (filters.location) {
      filtered = filtered.filter(car => car.location.toLowerCase().includes(filters.location.toLowerCase()));
    }

    if (filters.min_price) {
      filtered = filtered.filter(car => car.price_per_day >= parseFloat(filters.min_price));
    }

    if (filters.max_price) {
      filtered = filtered.filter(car => car.price_per_day <= parseFloat(filters.max_price));
    }

    setFilteredCars(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      type: '',
      location: '',
      min_price: '',
      max_price: ''
    });
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Loading cars...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="cars-page-title">
            Browse Our Fleet
          </h1>
          <p className="text-lg text-muted-foreground">Find the perfect car for your journey</p>
        </div>

        {/* Filters */}
        <div className="glass-card p-6 rounded-lg mb-12" data-testid="car-filters">
          <div className="grid md:grid-cols-5 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Car Type</label>
              <Select value={filters.type} onValueChange={(value) => handleFilterChange('type', value)}>
                <SelectTrigger data-testid="filter-type">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="SUV">SUV</SelectItem>
                  <SelectItem value="Sedan">Sedan</SelectItem>
                  <SelectItem value="Sports">Sports</SelectItem>
                  <SelectItem value="Luxury">Luxury</SelectItem>
                  <SelectItem value="Compact">Compact</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Location</label>
              <Input
                placeholder="e.g., Islamabad"
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                data-testid="filter-location"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Min Price (Rs./day)</label>
              <Input
                type="number"
                placeholder="0"
                value={filters.min_price}
                onChange={(e) => handleFilterChange('min_price', e.target.value)}
                data-testid="filter-min-price"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Max Price (Rs./day)</label>
              <Input
                type="number"
                placeholder="50000"
                value={filters.max_price}
                onChange={(e) => handleFilterChange('max_price', e.target.value)}
                data-testid="filter-max-price"
              />
            </div>

            <div className="flex items-end">
              <Button onClick={resetFilters} variant="outline" className="w-full" data-testid="filter-reset-btn">
                Reset Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Results count */}
        <div className="mb-6">
          <p className="text-muted-foreground" data-testid="cars-count">
            Showing {filteredCars.length} {filteredCars.length === 1 ? 'car' : 'cars'}
          </p>
        </div>

        {/* Car Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCars.map((car, index) => (
            <div key={car.id} className="car-card rounded-lg overflow-hidden fade-in stagger-item" data-testid={`car-card-${index}`}>
              <div className="aspect-video overflow-hidden">
                <img
                  src={car.image_url}
                  alt={car.name}
                  loading="lazy"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              
              <div className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-accent font-mono mb-1">{car.type}</p>
                    <h3 className="text-xl font-bold" data-testid={`car-name-${index}`}>{car.name}</h3>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary" data-testid={`car-price-${index}`}>Rs. {car.price_per_day.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">per day</p>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{car.description}</p>

                <div className="flex gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{car.seats}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Settings className="w-4 h-4" />
                    <span>{car.transmission}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Fuel className="w-4 h-4" />
                    <span>{car.fuel_type}</span>
                  </div>
                </div>

                <p className="text-sm mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <span className="text-muted-foreground">{car.location}</span>
                </p>

                <div className="flex gap-2">
                  <Link to={`/cars/${car.id}`} className="flex-1" data-testid={`car-view-details-${index}`}>
                    <Button className="w-full btn-primary">
                      View Details
                    </Button>
                  </Link>
                  <WhatsAppBookButton car={car} variant="compact" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredCars.length === 0 && (
          <div className="text-center py-20" data-testid="no-cars-message">
            <p className="text-xl text-muted-foreground">No cars found matching your filters.</p>
            <Button onClick={resetFilters} className="mt-4">Reset Filters</Button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CarsPage;
